(async () => {
  const base = await import(window.theme.modules.baseComponent);

  const GIFT_KEY = '_dcd_gift';
  const money = (cents, fmt) => {
    const f = window.Theme?.utils?.formatMoney;
    return f ? f(cents, fmt) : `$${(cents / 100).toFixed(2)}`;
  };
  const el = (tag, cls, txt) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  };
  const svg = (paths, extra = '') =>
    `<svg viewBox="0 0 24 24" aria-hidden="true" ${extra}>${paths}</svg>`;
  const ICON_TICK = svg('<path d="M5 13l4 4L19 7"/>');
  const ICON_MINUS = svg('<path d="M6 12h12"/>');
  const ICON_PLUS = svg('<path d="M12 6v12M6 12h12"/>');

  customElements.define(
    'dagon-cart-drawer',
    class extends base.default {
      elements = {
        scrim: '[data-dcd-scrim]',
        close: '[data-dcd-close]',
        count: '[data-dcd-count]',
        offer: '[data-dcd-offer]',
        status: '[data-dcd-status]',
        slots: '[data-dcd-slots]',
        scroll: '[data-dcd-scroll]',
        notice: '[data-dcd-notice]',
        items: '[data-dcd-items]',
        gift: '[data-dcd-gift]',
        foot: '[data-dcd-foot]',
        subtotal: '[data-dcd-subtotal]',
        savingsRow: '[data-dcd-savings-row]',
        savings: '[data-dcd-savings]',
        total: '[data-dcd-total]',
        checkout: '[data-dcd-checkout]',
      };

      render() {
        const cfg = this.querySelector('[data-dcd-config]');
        if (!cfg) return;
        try {
          this.c = JSON.parse(cfg.textContent);
        } catch (e) {
          return console.error('[dcd] bad config', e);
        }

        this.qualifying = new Set(this.c.qualifyingIds || []);
        this.gifts = (this.c.gifts || []).filter((g) => g.variants.length);
        this.pick = { productId: null, options: [] };
        this.pendingNotice = null;

        this.bind();
        this.refresh();
      }

      /* ---------------------------------------------------------------- */
      /* Wiring                                                           */
      /* ---------------------------------------------------------------- */

      bind() {
        this.$scrim?.addEventListener('click', () => this.setOpen(false));
        this.$close?.addEventListener('click', () => this.setOpen(false));
        this.$checkout?.addEventListener('click', () => this.goToCheckout());

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.hasAttribute('open')) this.setOpen(false);
        });

        // Any cart mutation elsewhere in the theme.
        this.listenTo('cartChange', () => this.refresh(), false);

        // Intercept add-to-cart forms site-wide.
        document.addEventListener(
          'submit',
          (e) => {
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;
            const action = form.getAttribute('action') || '';
            if (!action.includes('/cart/add')) return;
            e.preventDefault();
            e.stopPropagation();
            this.addFromForm(form);
          },
          true
        );

        // Cart links open the drawer instead of navigating.
        document.addEventListener('click', (e) => {
          const link = e.target.closest?.('a[href$="/cart"], a[href*="/cart?"]');
          if (!link || link.closest('dagon-cart-drawer')) return;
          e.preventDefault();
          this.setOpen(true);
        });
      }

      setOpen(open) {
        this.toggleAttribute('open', open);
        document.documentElement.style.overflow = open ? 'hidden' : '';
        if (open) this.refresh();
      }

      /* Submit a real POST to the cart route, the same way the theme's cart
         page does it. More robust than navigating to /checkout: it survives
         market and locale prefixes and carries cart notes and attributes. */
      goToCheckout() {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = this.c.cartUrl;
        form.hidden = true;

        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = '';
        form.appendChild(flag);

        document.body.appendChild(form);
        form.submit();
      }

      async addFromForm(form) {
        const data = new FormData(form);
        const id = data.get('id');
        if (!id) return;
        this.setAttribute('busy', '');
        await this.post(this.c.addUrl, {
          items: [{ id: Number(id), quantity: Number(data.get('quantity') || 1) }],
        });
        this.removeAttribute('busy');
        this.forwardEvent('cartChange', {});
        await this.refresh();
        this.setOpen(true);
      }

      /* ---------------------------------------------------------------- */
      /* State                                                            */
      /* ---------------------------------------------------------------- */

      async refresh() {
        const cart = await this.getCart();
        if (!cart) return;

        const giftLines = [];
        let earned = 0;

        cart.items.forEach((item, i) => {
          const line = i + 1;
          if (item.properties && item.properties[GIFT_KEY]) {
            giftLines.push({ line, item });
          } else if (this.qualifying.has(item.product_id)) {
            earned += item.quantity;
          }
        });

        const claimed = giftLines.reduce((n, g) => n + g.item.quantity, 0);

        // Entitlement dropped below what's claimed: pull the newest gifts.
        if (claimed > earned) {
          let excess = claimed - earned;
          for (const g of [...giftLines].reverse()) {
            if (excess <= 0) break;
            const take = Math.min(excess, g.item.quantity);
            await this.change(g.line, g.item.quantity - take);
            excess -= take;
          }
          this.pendingNotice = this.c.strings.removedNotice;
          this.forwardEvent('cartChange', {});
          return this.refresh();
        }

        this.cart = cart;
        this.earned = earned;
        this.claimed = claimed;
        this.giftLines = giftLines;
        this.open = earned - claimed;

        this.paint();
      }

      paint() {
        const s = this.c.strings;
        const cart = this.cart;

        if (this.$count) {
          this.$count.textContent = cart.item_count
            ? `${cart.item_count} item${cart.item_count === 1 ? '' : 's'}`
            : '';
        }

        // Notice
        if (this.$notice) {
          if (this.pendingNotice) {
            this.$notice.textContent = this.pendingNotice;
            this.$notice.hidden = false;
            this.pendingNotice = null;
          } else {
            this.$notice.hidden = true;
          }
        }

        // Offer strip
        if (this.$offer) {
          this.$offer.hidden = cart.item_count === 0;
          if (this.$status) {
            this.$status.innerHTML =
              this.earned === 0
                ? s.statusNone
                : this.open > 0
                ? (s.statusOpen || '').replace('[n]', `<em>${this.open}</em>`)
                : s.statusAllClaimed;
          }
          this.paintSlots();
        }

        this.paintItems();
        this.paintGift();
        this.paintFoot();
      }

      paintSlots() {
        if (!this.$slots) return;
        this.$slots.replaceChildren();
        const s = this.c.strings;
        for (let i = 0; i < this.earned; i++) {
          const done = i < this.claimed;
          const pill = el('span', 'dcd-slot');
          pill.setAttribute('data-state', done ? 'claimed' : 'open');
          if (done) pill.insertAdjacentHTML('afterbegin', ICON_TICK);
          pill.appendChild(
            el('span', null, ((done ? s.slotClaimed : s.slotOpen) || 'Gift [n]').replace('[n]', i + 1))
          );
          this.$slots.appendChild(pill);
        }
      }

      paintItems() {
        if (!this.$items) return;
        this.$items.replaceChildren();
        const s = this.c.strings;

        if (!this.cart.item_count) {
          const wrap = el('div', 'dcd-empty');
          wrap.appendChild(el('p', null, s.emptyText));
          const btn = el('button', 'dcd-confirm', s.continueShopping);
          btn.type = 'button';
          btn.style.width = 'auto';
          btn.addEventListener('click', () => {
            window.location.href = this.c.allProductsUrl;
          });
          wrap.appendChild(btn);
          this.$items.appendChild(wrap);
          return;
        }

        this.cart.items.forEach((item, i) => {
          const line = i + 1;
          // Display state must reflect what Shopify ACTUALLY discounted, not
          // which line we tagged. When gift-eligible products also sit in the
          // qualifying collection, Shopify may zero a different line than the
          // one the customer picked. The tag is bookkeeping; this is truth.
          const isFree = item.line_price === 0;
          const isDiscounted = isFree || item.total_discount > 0;
          const row = el('div', 'dcd-item');
          if (isDiscounted) row.setAttribute('data-gift', '');

          const media = el('div', 'dcd-item-media');
          if (item.image) {
            const img = el('img');
            img.src = item.image;
            img.alt = item.product_title;
            img.loading = 'lazy';
            media.appendChild(img);
          }
          row.appendChild(media);

          const mid = el('div');
          mid.appendChild(el('p', 'dcd-item-title', item.product_title));
          if (item.variant_title) {
            mid.appendChild(el('p', 'dcd-item-variant', item.variant_title));
          }
          if (isDiscounted) {
            mid.appendChild(el('span', 'dcd-item-flag', s.giftFlag));
          }

          const actions = el('div', 'dcd-item-actions');
          if (!isDiscounted) {
            const qty = el('div', 'dcd-qty');
            const dec = el('button');
            dec.type = 'button';
            dec.innerHTML = ICON_MINUS;
            dec.addEventListener('click', () => this.setQty(line, item.quantity - 1));
            const inc = el('button');
            inc.type = 'button';
            inc.innerHTML = ICON_PLUS;
            inc.addEventListener('click', () => this.setQty(line, item.quantity + 1));
            qty.append(dec, el('span', null, String(item.quantity)), inc);
            actions.appendChild(qty);
          }
          const rm = el('button', 'dcd-remove', 'Remove');
          rm.type = 'button';
          rm.addEventListener('click', () => this.setQty(line, 0));
          actions.appendChild(rm);
          mid.appendChild(actions);
          row.appendChild(mid);

          const price = el('div', 'dcd-item-price');
          if (isDiscounted) {
            price.appendChild(el('span', 'dcd-was', money(item.original_line_price, this.c.moneyFormat)));
            price.appendChild(
              el('span', 'dcd-free', isFree ? 'Free' : money(item.line_price, this.c.moneyFormat))
            );
          } else {
            price.appendChild(el('span', null, money(item.line_price, this.c.moneyFormat)));
          }
          row.appendChild(price);

          this.$items.appendChild(row);
        });
      }

      paintGift() {
        if (!this.$gift) return;
        this.$gift.replaceChildren();
        if (this.open <= 0 || !this.gifts.length) return;

        const s = this.c.strings;
        const sec = el('div', 'dcd-gift');
        sec.appendChild(el('div', 'dcd-gift-banner', s.banner));

        const head = el('div', 'dcd-gift-head');
        head.appendChild(el('h3', 'dcd-gift-name', (s.slotOpen || 'Gift [n]').replace('[n]', this.claimed + 1)));
        head.appendChild(el('span', 'dcd-gift-note', s.pickPrompt));
        sec.appendChild(head);

        // Style carousel
        const rail = el('div', 'dcd-rail');
        this.gifts.forEach((g) => {
          const inStock = g.variants.filter((v) => v.available);
          const card = el('button', 'dcd-card');
          card.type = 'button';
          if (!inStock.length) card.disabled = true;
          if (this.pick.productId === g.id) card.setAttribute('data-selected', '');

          const media = el('div', 'dcd-card-media');
          if (g.image) {
            const img = el('img');
            img.src = g.image;
            img.alt = g.title;
            img.loading = 'lazy';
            media.appendChild(img);
          }
          const tick = el('span', 'dcd-card-tick');
          tick.innerHTML = ICON_TICK;
          media.appendChild(tick);
          card.appendChild(media);

          card.appendChild(el('p', 'dcd-card-title', g.title));
          if (!inStock.length) {
            card.appendChild(el('p', 'dcd-card-oos', s.soldOut));
          } else {
            const pr = el('div', 'dcd-card-price');
            pr.appendChild(el('span', 'dcd-was', money(g.price, this.c.moneyFormat)));
            pr.appendChild(el('span', 'dcd-free', 'Free'));
            card.appendChild(pr);
          }

          card.addEventListener('click', () => {
            this.pick = { productId: g.id, options: [] };
            this.paintGift();
          });
          rail.appendChild(card);
        });
        sec.appendChild(rail);

        // Size row for the chosen style
        const chosen = this.gifts.find((g) => g.id === this.pick.productId);
        if (chosen) {
          const wrap = el('div', 'dcd-sizes-wrap');
          const names = chosen.optionNames || [];
          const depth = Math.max(0, names.length - 1); // last option = size
          const inStock = chosen.variants.filter((v) => v.available);

          wrap.appendChild(el('span', 'dcd-sizes-label', names[depth] || s.sizeLabel));

          const row = el('div', 'dcd-sizes');
          const values = [...new Set(chosen.variants.map((v) => v.options[depth]))];
          values.forEach((val) => {
            const has = inStock.some((v) => v.options[depth] === val);
            const b = el('button', 'dcd-size', val);
            b.type = 'button';
            if (!has) b.disabled = true;
            if (this.pick.options[depth] === val) b.setAttribute('data-selected', '');
            b.addEventListener('click', () => {
              this.pick.options[depth] = val;
              this.paintGift();
            });
            row.appendChild(b);
          });
          wrap.appendChild(row);

          const match = inStock.find((v) =>
            v.options.every((o, d) => (d === depth ? o === this.pick.options[depth] : true))
          );

          const confirm = el('button', 'dcd-confirm', s.confirm);
          confirm.type = 'button';
          confirm.disabled = !match;
          confirm.addEventListener('click', () => match && this.claim(match.id));
          wrap.appendChild(confirm);

          sec.appendChild(wrap);
        }

        this.$gift.appendChild(sec);
      }

      paintFoot() {
        if (!this.$foot) return;
        const cart = this.cart;
        this.$foot.hidden = !cart.item_count;
        if (!cart.item_count) return;

        this.$subtotal.textContent = money(cart.original_total_price, this.c.moneyFormat);
        this.$total.textContent = money(cart.total_price, this.c.moneyFormat);

        const saved = cart.total_discount || 0;
        if (this.$savingsRow) {
          this.$savingsRow.hidden = saved <= 0;
          if (this.$savings) {
            this.$savings.textContent = `− ${money(saved, this.c.moneyFormat)}`;
          }
        }
      }

      /* ---------------------------------------------------------------- */
      /* Cart I/O                                                         */
      /* ---------------------------------------------------------------- */

      async claim(variantId) {
        this.setAttribute('busy', '');
        await this.post(this.c.addUrl, {
          items: [{ id: variantId, quantity: 1, properties: { [GIFT_KEY]: 'yes' } }],
        });
        this.pick = { productId: null, options: [] };
        this.removeAttribute('busy');
        this.forwardEvent('cartChange', {});
        await this.refresh();
      }

      async setQty(line, quantity) {
        this.setAttribute('busy', '');
        await this.change(line, Math.max(0, quantity));
        this.removeAttribute('busy');
        this.forwardEvent('cartChange', {});
        await this.refresh();
      }

      change(line, quantity) {
        return this.post(this.c.changeUrl, { line, quantity });
      }

      async getCart() {
        try {
          const r = await fetch(`${this.c.cartUrl}.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          return await r.json();
        } catch (e) {
          console.error('[dcd] cart read failed', e);
          return null;
        }
      }

      async post(url, body) {
        try {
          const r = await fetch(`${url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          return await r.json();
        } catch (e) {
          console.error('[dcd] cart write failed', e);
          return null;
        }
      }
    }
  );
})();